import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { authTokenSelector } from 'redux/auth/selectors';
import Api from 'util/Api';

const COLLECTIONS_ENDPOINT = `${process.env.REACT_APP_MS_API_URL}/collections`;

export interface CollectionStore {
  marketplaceId: number;
  marketplaceName: string;
  storeId: number;
  storeName: string;
  address: string;
  lat: number;
  lng: number;
}

export interface CollectionDeposit {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

export interface CreateCollectionPayload {
  stores: CollectionStore[];
  driverId: number;
  driverName: string;
  driverDni: string;
  vehiclePlate: string;
  notifyStore: boolean;
  notifyDriver: boolean;
  deposit: CollectionDeposit | null;
}

const createCollection = async (
  payload: CreateCollectionPayload,
  token: string,
): Promise<unknown> => {
  const { data } = await Api.apiAxios.post(
    `${COLLECTIONS_ENDPOINT}/operation`,
    payload,
  );
  return data;
};

export const useCreateCollection = () => {
  const token = useSelector(authTokenSelector);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCollectionPayload) =>
      createCollection(payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
  });
};

// --- GET driver info ---

export interface DriverCollectionInfo {
  hasActiveCollection: boolean;
  activeCollectionId: number | null;
  lastCollectionAt: string | null;
  lastVehiclePlate: string | null;
}

const getDriverInfo = async (
  driverId: number,
  token: string,
): Promise<DriverCollectionInfo> => {
  const { data } = await Api.apiAxios.get(
    `${COLLECTIONS_ENDPOINT}/driver-info/${driverId}`,
  );
  return data;
};

export const useDriverCollectionInfo = (driverId: number | null) => {
  const token = useSelector(authTokenSelector);
  return useQuery({
    queryKey: ['driver-info', driverId],
    queryFn: () => getDriverInfo(driverId!, token),
    enabled: !!driverId,
  });
};

// --- GET collections ---

export type CollectionStatus =
  | 'pending_collection'
  | 'in_transit_to_store'
  | 'collected'
  | 'received'
  | 'cancelled';

export interface CollectionStoreItem {
  id: number;
  storeId: number;
  storeName: string;
  [key: string]: unknown;
}

export interface CollectionItem {
  collection_request_id: number;
  driver_id: number;
  driver_name: string;
  vehicle_plate: string;
  status: CollectionStatus;
  deposit_id: number | null;
  stores: CollectionStoreItem[];
  created_at: string;
}

export interface CollectionPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface GetCollectionsResponse {
  data: CollectionItem[];
  pagination: CollectionPagination;
}

export interface GetCollectionsParams {
  status?: CollectionStatus;
  q?: string;
  page?: number;
  pageSize?: number;
}

const getCollections = async (
  params: GetCollectionsParams,
  _token: string,
): Promise<GetCollectionsResponse> => {
  const { data } = await Api.apiAxios.get<GetCollectionsResponse>(
    COLLECTIONS_ENDPOINT + '/operation',
    {
      params,
    },
  );

  const { page = 1, pageSize = 20 } = params;
  const filtered = data.data || [];

  const total = filtered.length;
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);

  return {
    data: paged,
    pagination: {
      page,
      pageSize,
      total,
      totalPages,
      hasMore: page < totalPages,
    },
  };
};

// --- GET collection detail ---

export interface CollectionDetailStore {
  id: number;
  marketplaceId: number;
  marketplaceName: string;
  storeId: number;
  storeName: string;
  storeAddress: string;
  storeLat: number;
  storeLng: number;
  status: string;
  collectedAt: string | null;
  declaredPackageCount?: number;
  linkedShipmentsAmount?: number;
}

export interface CollectionGeofenceEvent {
  id: number;
  eventType: string;
  locationType: string;
  storeId: number | null;
  lat: number;
  lng: number;
  occurredAt: string;
}

export interface CollectionPickupLog {
  id: number;
  storeId: number | null;
  packageCount: number;
  lat: number;
  lng: number;
  occurredAt: string;
  photos?: string[];
}

export interface CollectionStatusLog {
  id: number;
  scope: string;
  collectionRequestStoreId: number | null;
  fromStatus: string;
  toStatus: string;
  actorType: string;
  actorId: number;
  actorName: string;
  reason: string | null;
  metadata: unknown | null;
  occurredAt: string;
}

export interface CollectionDetail {
  collectionRequestId: number;
  driverId: number;
  driverName: string;
  driverDni: string;
  vehiclePlate: string;
  notifyStore: boolean;
  notifyDriver: boolean;
  depositId: number | null;
  depositName: string | null;
  depositAddress: string | null;
  depositLat: number | null;
  depositLng: number | null;
  tenantId: number;
  status: CollectionStatus;
  createdAt: string;
  updatedAt: string;
  stores: CollectionDetailStore[];
  geofenceEvents: CollectionGeofenceEvent[];
  pickupLogs: CollectionPickupLog[];
  statusLogs: CollectionStatusLog[];
}

const getCollectionDetail = async (
  collectionId: number,
  token: string,
): Promise<CollectionDetail> => {
  const { data } = await Api.apiAxios.get<CollectionDetail>(
    `${COLLECTIONS_ENDPOINT}/operation/${collectionId}`,
  );
  return data;
};

export const useGetCollectionDetail = (collectionId: number | null) => {
  const token = useSelector(authTokenSelector);
  return useQuery({
    queryKey: ['collection-detail', collectionId],
    queryFn: () => getCollectionDetail(collectionId!, token),
    enabled: !!collectionId,
  });
};

// --- GET collection shipments ---

export interface CollectionShipment {
  id: number;
  collection_request_id: number;
  collection_request_store_id: number;
  external_code: string;
  created_at: string;
}

const getCollectionShipments = async (
  collectionId: number,
  token: string,
): Promise<CollectionShipment[]> => {
  const { data } = await Api.apiAxios.get<CollectionShipment[]>(
    `${COLLECTIONS_ENDPOINT}/operation/${collectionId}/shipments`,
  );
  return Array.isArray(data) ? data : [data];
};

export const useGetCollectionShipments = (
  collectionId: number | null,
  enabled: boolean,
) => {
  const token = useSelector(authTokenSelector);
  return useQuery({
    queryKey: ['collection-shipments', collectionId],
    queryFn: () => getCollectionShipments(collectionId!, token),
    enabled: !!collectionId && enabled,
  });
};

const cancelCollection = async (
  collectionId: number,
  token: string,
): Promise<unknown> => {
  const { data } = await Api.apiAxios.delete(
    `${COLLECTIONS_ENDPOINT}/operation/${collectionId}`,
  );
  return data;
};

export const useCancelCollection = () => {
  const token = useSelector(authTokenSelector);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (collectionId: number) => cancelCollection(collectionId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
  });
};

export interface ArrivalResponse {
  shipments_amount: number;
}

const fulfillCollectionStore = async (
  collectionId: number,
  storeId: number,
  token: string,
): Promise<unknown> => {
  const { data } = await Api.apiAxios.put(
    `${COLLECTIONS_ENDPOINT}/operation/${collectionId}/fulfill/${storeId}`,
    {},
  );
  return data;
};

export const useFulfillCollectionStore = () => {
  const token = useSelector(authTokenSelector);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      collectionId,
      storeId,
    }: {
      collectionId: number;
      storeId: number;
    }) => fulfillCollectionStore(collectionId, storeId, token),
    onSuccess: (_data, { collectionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['collection-detail', collectionId],
      });
    },
  });
};

export const notifyCollectionArrival = async (
  collectionId: number,
  storeId: number,
  externalcode: string,
  token: string,
): Promise<ArrivalResponse | null> => {
  try {
    const { data } = await Api.apiAxios.put<ArrivalResponse>(
      `${COLLECTIONS_ENDPOINT}/operation/${collectionId}/arrive/${storeId}`,
      { externalcode },
    );
    return data;
  } catch (_e) {
    return null;
  }
};

export const useGetCollections = (params: GetCollectionsParams) => {
  const token = useSelector(authTokenSelector);
  return useQuery({
    queryKey: ['collections', params],
    queryFn: () => getCollections(params, token),
    initialData: {
      data: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0,
        hasMore: false,
      },
    },
  });
};
